import { useEffect } from "react";
import { Link, Navigate, useLocation, useNavigationType } from "react-router-dom";
import { AlertTriangle, ShieldAlert } from "lucide-react";
import { serverTimestamp } from "firebase/firestore";
import Navbar from "../Landing/Navbar";
import { auth, db } from "../firebase";
import { updateLastActive } from "../utils/activityTracking";
import { clearStoredAuthSession } from "../utils/authSession";
import useAuthAccess from "../hooks/useAuthAccess";

function AccessBlockedScreen({ profile }) {
  const status = profile?.status || "active";
  const isBlocked = status === "blocked" || Boolean(profile?.banned);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="page-container flex min-h-[calc(100vh-80px)] flex-col items-center justify-center py-10">
        <div className="w-full max-w-md text-center">
          {/* Icon */}
          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-rose-50">
            <ShieldAlert size={36} className="text-rose-500" />
          </div>

          {/* Title */}
          <h1 className="mt-6 font-display text-2xl font-bold text-ink-800 sm:text-3xl">
            Account {isBlocked ? "Blocked" : "Suspended"}
          </h1>
          <p className="mt-3 text-[14px] leading-7 text-ink-500">
            {isBlocked
              ? "Your account has been blocked by the admin team. If you believe this is a mistake, please contact support."
              : "Your account is temporarily suspended. Contact the admin team to restore access."}
          </p>

          {/* Info card */}
          <div className="mt-6 rounded-2xl border border-sand-200 bg-sand-50 p-4 text-left">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-[12.5px] text-ink-400">Status</span>
                <span className="rounded-full bg-rose-50 px-2.5 py-0.5 text-[11px] font-bold uppercase text-rose-600">
                  {isBlocked ? "Blocked" : "Suspended"}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[12.5px] text-ink-400">Profile ID</span>
                <span className="text-[13px] font-semibold text-ink-800">{profile?.kmProfileId || "-"}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[12.5px] text-ink-400">Registered number</span>
                <span className="text-[13px] font-semibold text-ink-800">{profile?.mobileNumber || "-"}</span>
              </div>
            </div>
          </div>

          {/* Help text */}
          <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-left">
            <div className="flex items-start gap-2.5">
              <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-600" />
              <div className="text-[12.5px] leading-5 text-amber-800">
                <p className="font-semibold">What you can do:</p>
                <ul className="mt-1 list-disc pl-4 space-y-0.5">
                  <li>Contact admin via WhatsApp or phone</li>
                  <li>Share your Profile ID for faster resolution</li>
                  <li>Wait for admin to review and unblock</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 grid grid-cols-2 gap-3">
            <Link
              to="/"
              className="btn-ghost py-3 text-[13px]"
            >
              Go to home
            </Link>
            <button
              type="button"
              onClick={() => {
                clearStoredAuthSession();
                auth.signOut();
              }}
              className="btn-primary py-3 text-[13px]"
            >
              Logout
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}

// Pages that a logged-in user without a profile is still allowed to visit
const PROFILE_EXEMPT_PATHS = ["/create-profile", "/my-profile"];

// A profile is considered "created" when it has at least a fullName saved
function isProfileCreated(profile) {
  return Boolean(profile?.fullName);
}

function isRecentProfileSubmit(uid) {
  try {
    const raw = sessionStorage.getItem("karankiProfileJustSubmitted");
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    if (uid && parsed?.uid !== uid) return false;
    const at = Number(parsed?.at || 0);
    return at > 0 && Date.now() - at < 15000;
  } catch {
    return false;
  }
}

const RestrictedRoute = ({ children }) => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const { loading, user, role, profile, isBlocked } = useAuthAccess();

  useEffect(() => {
    if (!profile?.id || !user) return;

    const navigationKind = navigationType === "POP" ? "browser-back-forward" : navigationType.toLowerCase();
    const syncActivity = (force = false) =>
      updateLastActive(db, profile.id, location.pathname, force, {
        lastNavigationAt: serverTimestamp(),
        lastNavigationPage: location.pathname,
        lastNavigationType: navigationKind,
      }).catch(() => {});

    syncActivity(true);

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        syncActivity();
      }
    };

    const handleActivity = () => {
      syncActivity();
    };

    window.addEventListener("focus", handleActivity);
    window.addEventListener("click", handleActivity);
    window.addEventListener("keydown", handleActivity);
    document.addEventListener("visibilitychange", handleVisibility);
    const heartbeatId = window.setInterval(() => {
      syncActivity();
    }, 30000);

    return () => {
      window.clearInterval(heartbeatId);
      window.removeEventListener("focus", handleActivity);
      window.removeEventListener("click", handleActivity);
      window.removeEventListener("keydown", handleActivity);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [profile?.id, user, location.pathname, navigationType]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#fffaf6]">
        <div className="h-14 w-14 animate-spin rounded-full border-4 border-[#c90243] border-t-transparent" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (role === "admin") {
    return <Navigate to="/admin/dashboard" replace />;
  }

  if (isBlocked) {
    return <AccessBlockedScreen profile={profile} />;
  }

  // If the user has no profile yet, redirect to create-profile
  // (exempt the create-profile and my-profile pages themselves)
  const isExempt = PROFILE_EXEMPT_PATHS.includes(location.pathname);
  const allowDashboardGrace =
    location.pathname === "/dashboard" && isRecentProfileSubmit(user?.uid);

  // Once a profile exists, the create-profile URL should no longer be shown.
  // Keep /my-profile available as the edit route.
  if (location.pathname === "/create-profile" && isProfileCreated(profile)) {
    return <Navigate to="/dashboard" replace />;
  }

  if (!isExempt && !isProfileCreated(profile)) {
    if (allowDashboardGrace) {
      return children;
    }
    return <Navigate to="/create-profile" replace />;
  }

  return children;
};

export default RestrictedRoute;
